// utils/bugLog.js
// Lightweight, dependency-free capture buffer that backs the in-app
// "Report a bug" button (see components/BugReporter.jsx).
//
// Two ring buffers are kept in module scope:
//   • logBuffer   — the last N console.{log,info,warn,error,debug} calls
//   • eventBuffer — window 'error' / 'unhandledrejection' + app:cold-start-retry
//
// installBugLog() patches console.* once (idempotent) and registers the window
// listeners. It's called at app boot (index.js) BEFORE React renders, so the
// buffer captures everything from first paint. The original console methods are
// always called through, so dev-tools behavior is unchanged.
//
// Designed to be lifted verbatim into the other SWAs — no app-specific imports.

const LOG_LIMIT = 120;      // most recent console lines kept
const EVENT_LIMIT = 40;     // most recent error/lifecycle events kept
const MAX_ARG_LEN = 2000;   // per-argument serialization cap (chars)

const logBuffer = [];
const eventBuffer = [];
let installed = false;

function push(buf, entry, limit) {
  buf.push(entry);
  if (buf.length > limit) buf.splice(0, buf.length - limit);
}

// Best-effort, never-throw serialization of a single console argument.
function serializeArg(arg) {
  if (arg == null) return String(arg);
  if (typeof arg === 'string') return arg.length > MAX_ARG_LEN ? arg.slice(0, MAX_ARG_LEN) + '…' : arg;
  if (typeof arg === 'number' || typeof arg === 'boolean') return String(arg);
  if (arg instanceof Error) return `${arg.name}: ${arg.message}${arg.stack ? '\n' + arg.stack : ''}`.slice(0, MAX_ARG_LEN);
  try {
    const seen = new WeakSet();
    const s = JSON.stringify(arg, (k, v) => {
      if (typeof v === 'object' && v !== null) {
        if (seen.has(v)) return '[Circular]';
        seen.add(v);
      }
      return v;
    });
    return s == null ? String(arg) : (s.length > MAX_ARG_LEN ? s.slice(0, MAX_ARG_LEN) + '…' : s);
  } catch {
    try { return String(arg).slice(0, MAX_ARG_LEN); } catch { return '[unserializable]'; }
  }
}

export function installBugLog() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const levels = ['log', 'info', 'warn', 'error', 'debug'];
  for (const level of levels) {
    const original = console[level] ? console[level].bind(console) : null;
    console[level] = (...args) => {
      try {
        push(logBuffer, {
          level,
          ts: new Date().toISOString(),
          msg: args.map(serializeArg).join(' '),
        }, LOG_LIMIT);
      } catch { /* never let logging break the app */ }
      if (original) original(...args);
    };
  }

  window.addEventListener('error', (e) => {
    push(eventBuffer, {
      type: 'window.error',
      ts: new Date().toISOString(),
      message: e?.message || String(e),
      source: e?.filename || null,
      line: e?.lineno ?? null,
      col: e?.colno ?? null,
      stack: e?.error?.stack ? String(e.error.stack).slice(0, MAX_ARG_LEN) : null,
    }, EVENT_LIMIT);
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e?.reason;
    push(eventBuffer, {
      type: 'unhandledrejection',
      ts: new Date().toISOString(),
      message: reason instanceof Error ? `${reason.name}: ${reason.message}` : serializeArg(reason),
      stack: reason instanceof Error && reason.stack ? String(reason.stack).slice(0, MAX_ARG_LEN) : null,
    }, EVENT_LIMIT);
  });

  // App-level signal already emitted by schedApi's cold-start retry layer.
  window.addEventListener('app:cold-start-retry', (e) => {
    push(eventBuffer, {
      type: 'cold-start-retry',
      ts: new Date().toISOString(),
      message: `Cold-start retry: ${e?.detail?.label || 'request'}`,
    }, EVENT_LIMIT);
  });
}

// Record a structured event from anywhere in the app.
//
// The window 'error' / 'unhandledrejection' listeners above only ever see
// UNCAUGHT failures, and this codebase is deliberate about catching things —
// `.catch(() => [])` on reads, try/catch around writes, a fetch interceptor that
// swallows the edge 401. That discipline is right, but it meant the event buffer
// was empty on all 68 bug reports ever filed across all four apps: by the time a
// user hits "Report a bug", every trace of what went wrong has been handled away.
// This is how a caller hands the buffer a breadcrumb on the way past.
//
// Never throws and never blocks — diagnostics must not be able to break the app.
export function recordEvent(type, fields = {}) {
  try {
    push(eventBuffer, { type, ts: new Date().toISOString(), ...fields }, EVENT_LIMIT);
  } catch { /* diagnostics are best-effort */ }
}

// Snapshots — return copies so callers can't mutate the live buffers.
export function snapshotLogs() { return logBuffer.slice(); }
export function snapshotEvents() { return eventBuffer.slice(); }

// Stable client-side context anyone can attach to a report.
export function snapshotClientContext() {
  if (typeof window === 'undefined') return {};
  return {
    url: window.location.href,
    route: window.location.pathname + window.location.search + window.location.hash,
    referrer: document.referrer || null,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1,
    },
    screen: { width: window.screen?.width ?? null, height: window.screen?.height ?? null },
    userAgent: navigator.userAgent,
    language: navigator.language,
    online: navigator.onLine,
    capturedAt: new Date().toISOString(),
  };
}
